import { useCallback, useState } from "react";
import { Box, Button, Flex, HStack, Icon, Input, ResponsiveValue, Text, Tooltip } from "@chakra-ui/react";
import SignalStrengthIndicator from "@tether-web-portal/components/indicators/SignalStrengthIndicator";
import { BatteryLevel } from "../BatteryLevel";
import { GraphColor } from "@tether-web-portal/types/Room";
import { BsPencil } from "react-icons/bs";
import { RoomColorItem, RoomColorPicker } from "@tether-web-portal/components/create-room/RoomColorPicker";
import { formatDeviceName } from "@tether-web-portal/utils/formatDeviceName";
import { SecondaryButton } from "@tether-web-portal/components/button/SecondaryButton";
import { DeviceType } from "@tether-web-portal/types/DeviceType";
import { DeviceIcon } from "@tether-web-portal/components/device/DeviceIcon";
import { RoomLocationWithReading } from "@tether-web-portal/types/RoomLocation";
import { OutsideWithReading } from "@tether-web-portal/types/OutsideWithReading";
import { SampleBreakdown } from "@tether-web-portal/components/areas/location-reading/makeSampleBreakdown";

export const RoomLocationEditableHeader = ({
  editMode,
  breakdown,
  roomLocation,
  px,
  mt,
  onLiveCountsClick,
  onChangeLocation,
}: {
  editMode?: boolean;
  breakdown?: SampleBreakdown;
  roomLocation: RoomLocationWithReading | OutsideWithReading;
  px?: ResponsiveValue<string>;
  mt?: ResponsiveValue<string>;
  onLiveCountsClick: () => void;
  onChangeLocation: (
    id: string,
    input: Partial<RoomLocationWithReading | OutsideWithReading>,
  ) => Promise<void>;
}) => {
  const deviceType = roomLocation.device?.type;

  const [formName, setFormName] = useState(roomLocation.name);
  const [formColor, setFormColor] = useState(roomLocation.color);

  const [shouldEditName, setShouldEditName] = useState(Boolean(editMode));
  const [shouldEditColor, setShouldEditColor] = useState(false);

  const [nameInputValue, setNameInputValue] = useState(roomLocation.name);

  const onNameChange = useCallback(async () => {
    const revertNameOnError = formName;
    setShouldEditName(false);
    setFormName(nameInputValue);
    try {
      await onChangeLocation(roomLocation.id, { name: nameInputValue });
    } catch {
      setFormName(revertNameOnError);
    }
  }, [onChangeLocation, roomLocation.id, nameInputValue, formName]);

  const onColorChange = useCallback(
    async (color: GraphColor) => {
      const revertColorOnError = formColor;
      setShouldEditColor(false);
      setFormColor(color);
      try {
        await onChangeLocation(roomLocation.id, { color });
      } catch {
        setFormColor(revertColorOnError);
      }
    },
    [onChangeLocation, roomLocation.id, formColor],
  );

  return (
    <Box w="100%">
      <Flex mt={mt} px={px} flexDir="row" justifyContent="space-between">
        <HStack>
          <RoomColorItem
            color={formColor as GraphColor}
            isSelected={shouldEditColor}
            onClick={() => {
              setShouldEditColor(!shouldEditColor);
            }}
          />

          <Tooltip label={formatDeviceName(roomLocation.device?.type)}>
            <Flex>
              <DeviceIcon type={roomLocation.device?.type} />
            </Flex>
          </Tooltip>

          <HStack ml="4px">
            {Boolean(!shouldEditName) && (
              <Text
                fontSize="16px"
                color="gray.500"
                fontWeight={"bold"}
                overflow="hidden"
                textOverflow={"ellipsis"}
                maxWidth={300}
              >
                {formName}
              </Text>
            )}

            {Boolean(shouldEditName) && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onNameChange();
                }}
              >
                <Input
                  autoFocus={true}
                  value={nameInputValue}
                  onChange={(e) => setNameInputValue(e.target.value)}
                  w={240}
                />
              </form>
            )}

            <>
              {Boolean(!shouldEditName) && (
                <Button variant={"link"} onClick={() => setShouldEditName(true)}>
                  <Icon as={BsPencil} />
                </Button>
              )}
              {Boolean(shouldEditName) && (
                <Button variant={"link"} onClick={onNameChange}>
                  Save
                </Button>
              )}
            </>
          </HStack>
        </HStack>
        <Flex ml="auto" alignItems="center">
          {deviceType === DeviceType.ROOM_RADAR && (
            <SecondaryButton mr={4} onClick={onLiveCountsClick} title="View Live Counts" />
          )}
          {!breakdown?.isReallyOld && breakdown?.battery !== undefined && (
            <Box pl={2} pr={2} borderRadius={10} mt={1} mr="4px" display="flex" alignItems="center">
              <BatteryLevel battery={breakdown.battery} />
            </Box>
          )}

          {Boolean(!breakdown?.isReallyOld && typeof breakdown?.signalStrengthPercentage !== "undefined") && (
            <SignalStrengthIndicator signalStrength={breakdown!.signalStrengthPercentage!} />
          )}
        </Flex>
      </Flex>
      {shouldEditColor && (
        <Box px={5}>
          <RoomColorPicker onSelectColor={onColorChange} selectedColor={formColor} />
        </Box>
      )}
    </Box>
  );
};
